import React, { useState } from 'react';

// Google Sheets fallback (existing integration, kept as secondary store)
const SHEETS_URL =
  'https://script.google.com/macros/s/AKfycby79QaTT9rqIZsBYctEkA69Pyhqc7Uxw4S5IZr8oqjPVKZs7d-mQt_i3vd7BF6VMa7v/exec';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Field names must be lowercase to satisfy here.now's collection schema.
    const payload = {
      name:        formData.name,
      email:       formData.email,
      company:     formData.company,
      message:     formData.message,
      submittedat: new Date().toISOString(),
    };

    let succeeded = false;

    // Primary: here.now secure data store (server-side enforced, owner-read-only).
    // Works when deployed; skipped silently in local dev (different origin).
    try {
      const idempotencyKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await fetch('/.herenow/data/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) succeeded = true;
    } catch (_) {
      // Not on here.now (e.g., localhost) — fall through to secondary
    }

    // Secondary: Google Sheets (opaque no-cors, works from any origin)
    try {
      const res = await fetch(SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.type === 'opaque') succeeded = true;
    } catch (_) {
      // Sheets unavailable
    }

    if (succeeded) {
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } else {
      setError('There was an error submitting your message. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {isSubmitted ? (
        <div className="bg-green-50 text-green-700 px-6 py-4 rounded-lg shadow-lg" role="alert">
          <p className="font-bold mb-2">Thank you for reaching out!</p>
          <p>We've received your message and will get back to you shortly.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                required
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              required
            />
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded-lg" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 rounded-lg text-white font-medium transition-all duration-300
              ${isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}
    </div>
  );
};

const Contact = () => (
  <section id="contact" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
          Get In Touch
        </span>
        <h2 className="text-4xl font-bold mt-2 mb-4">
          Start Your AI Journey Today
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Ready to transform your business with AI? Contact us to discuss your needs.
        </p>
      </div>
      <div className="max-w-2xl mx-auto">
        <ContactForm />
      </div>
      <div className="mt-12 text-center">
        <p className="font-semibold text-lg">Prefer email? Reach us directly at:</p>
        <a href="mailto:aksoumya@rayosense.com" className="text-blue-500 hover:text-blue-700 text-xl font-bold">
          info@rayosense.com
        </a>
      </div>
    </div>
  </section>
);

export default Contact;
